import { useContext, useEffect, useState } from 'react'

// Utils
import getUserInitialsData from '../../utils/data/getUserInitialsData'
import getUserTeamSeasonsData from '../../utils/data/getUserTeamSeasonsData'
import setTeamAgeIdToFilters from '../../utils/data/setTeamAgeIdToFilters'
import { getLocalStorage, setLocalStorage } from '../../utils/store/localstorage'

// Internal context
import { ModalContext } from '../../context/ModalContext'
import { UserContext } from '../../context/UserContext'
import { FilterContext } from '../../context/FilterContext'

const loginDateStorageKey = 'loginDateStorage'

const UserController = () => {
  // Contexts
  const modalContext = useContext(ModalContext)
  const userContext = useContext(UserContext)
  const filterContext = useContext(FilterContext)

  // States
  const [sessionState, setSessionState] = useState(false)
  const [eventsChecked, setCheckedEvents] = useState(false)

  useEffect(() => {
    if (!userContext.loadedSession || !userContext.loggedIn || userContext?.userSession?.code === 401) return false
    if (sessionState !== userContext?.userSession) setSessionState(userContext.userSession)
  }, [userContext])

  useEffect(() => {
    if (!sessionState || sessionState?.code === 401 || sessionState?.code === 0) return false
    getUserSessionEvents()
  }, [sessionState])

  useEffect(() => {
    if (userContext.userSession === false) return

    if (!userContext.userInitials) {
      const initials = getUserInitialsData(userContext.userSession)
      userContext.setUserInitials(initials || '')
    }

    if (userContext.loggedIn === true) {
      const loginDateStored = getLocalStorage(loginDateStorageKey)
      if (!loginDateStored) {
        setLocalStorage(loginDateStorageKey, new Date())
        setTeamAgeIdToFilters(userContext, filterContext)
      }
    }
  }, [userContext.userSession])

  useEffect(() => {
    userContext.updateSession()
    setSessionState(userContext.userSession)
  }, [modalContext.modalHook])

  const getUserSessionEvents = async () => {
    const event = await checkForUserSessionEvent()
    const modalPropsObj = getModalPropsObject(event)
    if (modalPropsObj) modalContext.openModal(modalPropsObj)
  }

  // const shouldShowModalDrawingapp = () => {
  //   const check = (!sessionState?.hooks?.drawingApp && sessionState?.hooks?.firstTime && sessionState?.hooks?.rinusOnboarding && sessionState?.teams?.length >= 0)
  //   return check
  // }

  const shouldShowModalMatchmode = () => {
    const check =
      process.env.NEXT_PUBLIC_MATCH_MODE === 'true' &&
      !sessionState?.hooks?.matchmode &&
      sessionState?.hooks?.firstTime &&
      sessionState?.hooks?.rinusOnboarding &&
      sessionState?.teams?.length >= 0
    return check
  }

  const shouldShowModalOnboarding = () => {
    const check = !sessionState?.hooks?.rinusOnboarding && !sessionState?.hooks?.firstTime && !sessionState?.teams
    return check
  }

  const shouldShowModalFirstTime = () => {
    const check = !sessionState?.hooks?.firstTime && sessionState?.teams?.length >= 0
    return check
  }

  const shouldShowModalCreateTeam = () => {
    const check = !sessionState?.teams || sessionState?.teams?.length <= 0
    return check
  }

  const shouldShowModalAddSchedule = () => {
    const check = sessionState?.team && sessionState?.team?.schedule?.activeDays.length <= 0
    return check
  }

  const shouldShowModalAddTeamToNewSeason = async () => {
    // TODO: block rendering the modal to add a new team to a new season many times
    if (eventsChecked) return false

    // Check if user does not have a team on current season
    if (
      sessionState?.hooks?.season20212022 &&
      sessionState?.hooks?.firstTime &&
      sessionState?.hooks?.rinusOnboarding &&
      sessionState?.teams?.length >= 1
    ) {
      const teamSeasonsData = await getUserTeamSeasonsData(sessionState)
      return !teamSeasonsData.activeSeasonPresent
    }

    if (
      !sessionState?.hooks?.season20212022 &&
      !sessionState?.hooks?.offseason20212022 &&
      sessionState?.hooks?.rinusOnboarding &&
      sessionState?.hooks?.firstTime &&
      sessionState?.teams?.length >= 1
    ) {
      // TODO comment it out since we set this above
      const teamSeasonsData = await getUserTeamSeasonsData(sessionState)
      if (teamSeasonsData?.activeSeasonPresent) {
        modalContext.updateModalHooks({ season20212022: true })
        modalContext.updateModalHooks({ offseason20212022: true })
        return false
      }
      return !teamSeasonsData?.activeSeasonPresent
    } else {
      return false
    }
  }

  // const shouldShowModalUpdateTeamOffseason = async () => {
  //   if (sessionState?.hooks?.season20212022 && !sessionState?.hooks?.offseason20212022 && sessionState?.hooks?.rinusOnboarding && sessionState?.hooks?.firstTime && sessionState?.teams?.length >= 1) {
  //     const teamSeasonsData = await getUserTeamSeasonsData(sessionState)
  //     if (teamSeasonsData?.activeClubIdEnabled) {
  //       modalContext.updateModalHooks({ offseason20212022: true })
  //       return false
  //     } else {
  //       return (!teamSeasonsData?.activeClubIdEnabled)
  //     }
  //   } else {
  //     return false
  //   }
  // }

  const shouldShowModalSelectTeam = () => {
    return !sessionState?.team && (sessionState?.teams?.length ?? 0) > 1
  }

  const checkForUserSessionEvent = async () => {
    let event = false
    if (shouldShowModalSelectTeam()) event = 'selectTeam'
    if (shouldShowModalAddSchedule()) event = 'addSchedule'
    if (shouldShowModalCreateTeam()) event = 'createteam'
    if (shouldShowModalFirstTime()) event = 'firsttime'
    if (shouldShowModalOnboarding()) event = 'onboarding'
    // if (shouldShowModalDrawingapp()) event = 'drawingapp'
    if (shouldShowModalMatchmode()) event = 'matchmode'
    if (await shouldShowModalAddTeamToNewSeason()) event = 'teamExpired'
    // if (await shouldShowModalUpdateTeamOffseason()) event = 'teamOffseason'
    setCheckedEvents(true)
    return event
  }

  const getModalPropsObject = (event) => {
    switch (event) {
      case 'updateapp':
        return { type: 'api', route: 'get/user/updateapp', modifier: 'Modal--onboarding', mandatory: true }
      case 'onboarding':
        return { type: 'api', route: 'get/user/onboarding', modifier: 'Modal--onboarding', mandatory: true }
      case 'firsttime':
        return { type: 'api', route: 'get/user/firsttime', modifier: 'Modal--onboarding', mandatory: true }
      // case 'drawingapp':
      //   return { type: 'api', route: 'get/user/drawingapp', modifier: 'Modal--onboarding', mandatory: true }
      case 'matchmode':
        return { type: 'api', route: 'get/user/matchmode', modifier: 'Modal--onboarding', mandatory: true }
      case 'createteam':
        return { type: 'api', route: 'create/team/open', mandatory: true }
      case 'addSchedule':
        return { type: 'api', route: `edit/trainingdays/${sessionState?.team?.id}`, mandatory: true }
      case 'selectTeam':
        return { type: 'api', route: 'edit/activeteam/open', mandatory: true }
      case 'teamExpired':
        return { type: 'api', route: 'get/user/teamexpired', modifier: 'Modal--onboarding', mandatory: true }
      case 'teamOffseason':
        return { type: 'api', route: 'get/user/teamoffseason', modifier: 'Modal--onboarding', mandatory: true }

      default:
        return false
    }
  }

  // pure functional component
  return <></>
}

export default UserController
